import { CaretDown } from '@phosphor-icons/react';
import classNames from 'classnames';
import Spinner from 'components/spinner/Spinner';
import Tooltip, { TooltipProps } from 'components/Tooltip';
import React, { ReactNode, useMemo } from 'react';
import { To, useNavigate } from 'react-router-dom';
import { Size, Type } from 'types';

export type ButtonType = Type | 'dangerOutline';

interface Props {
  size?: Size;
  type?: ButtonType;
  loading?: boolean;
  disabled?: boolean;
  active?: boolean;
  responsive?: boolean;
  className?: string;
  onClick?: () => void;
  htmlType?: 'submit' | 'reset' | 'button' | undefined;
  to?: To;
  tabIndex?: number;
  fullWidth?: boolean;
  icon?: ReactNode;
  iconShowOn?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  spinnerInverse?: boolean;
  loadingPosition?: 'left' | 'right';
  children: React.ReactNode;
  htmlForm?: string;
  tooltip?: TooltipProps;
  showDropdownIcon?: boolean;
}

export default function Button({
  children,
  type = 'default',
  size = 'medium',
  htmlType = 'button',
  loading,
  disabled,
  active,
  responsive = true,
  onClick,
  className,
  to,
  tabIndex,
  icon,
  iconShowOn = 'xs',
  fullWidth,
  spinnerInverse,
  loadingPosition = 'right',
  htmlForm,
  tooltip,
  showDropdownIcon,
}: Props): JSX.Element {
  const inverseSpinnerColor = spinnerInverse === undefined ? type === 'default' : false;
  const isDisabled = !!loading || !!disabled;

  const navigate = useNavigate();

  /**
   * render the icon via the Icon comp
   */
  const renderedIcon = useMemo(() => {
    if (!icon || size === 'small') return;
    return (
      <span
        className={classNames('hidden mr-1', {
          'xs:block': iconShowOn === 'xs',
          'sm:block': iconShowOn === 'sm',
          'md:block': iconShowOn === 'md',
          'lg:block': iconShowOn === 'lg',
          'xl:block': iconShowOn === 'xl',
          '2xl:block': iconShowOn === '2xl',
        })}
      >
        {icon}
      </span>
    );
  }, [icon, iconShowOn, size]);

  /**
   * Event when the user click on the button
   */
  const click = () => {
    if (to) {
      navigate(to);
    } else {
      onClick?.();
    }
  };

  const buttonElement = (
    <button
      form={htmlForm}
      type={htmlForm ? 'submit' : htmlType}
      className={classNames(
        'bg-clip-padding', // https://stackoverflow.com/questions/71123245/firefox-scaling-issues-causes-pixelated-blurry-borders
        'inline-flex',
        'justify-center',
        'items-center',
        'border border-gray-300 dark:border-gray-500',
        'font-medium',
        {
          'rounded-md': type !== 'link',
        },
        {
          'sm:w-auto': !fullWidth,
          'w-full': responsive || fullWidth,
          'shadow-sm': type !== 'link',
        },
        // Disabled
        {
          'cursor-default': isDisabled,
          'cursor-not-allowed': isDisabled,
          'disabled:opacity-50': isDisabled && !active,
        },
        // Small font size
        {
          'text-xs': size === 'small',
          'px-2': size === 'small',
          'py-1': size === 'small',
        },
        // Medium font size
        {
          'text-sm': size === 'medium',
          'px-4': size === 'medium' && !icon,
          'px-3': size === 'medium' && icon,
          'py-2': size === 'medium',
        },
        // Large font size
        {
          'text-lg': size === 'large',
          'px-6': size === 'large' && !icon,
          'px-4': size === 'large' && icon,
          'py-3': size === 'large',
        },
        // Primary color classes
        {
          'text-white bg-primary hover:bg-opacity-90': type === 'primary',
          'bg-opacity-70': type === 'primary' && active,
        },
        // Default color classes
        {
          'text-gray-600 border-gray-300 bg-white hover:bg-gray-50': type === 'default',
          'bg-gray-50 border-gray-400': type === 'default' && active,
        },
        // Danger color classes
        {
          'text-white bg-danger-600 hover:bg-danger-700': type === 'danger',
          'bg-danger-700': type === 'danger' && active,
        },
        // Danger-outline color classes
        {
          'text-danger-600 border-danger-300 bg-white hover:bg-danger-50': type === 'dangerOutline',
          'bg-danger-50 border-danger-400': type === 'dangerOutline' && active,
        },
        // Default color classes
        {
          'text-gray-600 dark:text-gray-200 hover:text-primary border-0': type === 'link',
          'text-gray-800': type === 'link' && active,
        },
        className,
      )}
      onClick={click}
      disabled={isDisabled}
      tabIndex={tabIndex}
    >
      {renderedIcon}
      {loading && loadingPosition === 'left' && <Spinner className='ml-2' inverseColor={inverseSpinnerColor} size={size} />}
      {children}
      {loading && loadingPosition === 'right' && <Spinner className='ml-2' inverseColor={inverseSpinnerColor} size={size} />}
      {showDropdownIcon && (
        <span className='inline-block pl-3'>
          <CaretDown />
        </span>
      )}
    </button>
  );

  // Warn the user when he use both htmlForm and onClick
  // todo is this needed
  if (htmlForm && onClick) {
    console.warn('[Button]: You are mixing both the "onClick" and the "htmlForm" property. This can lead to submitting a form twice.');
  }

  if (tooltip) {
    return (
      <Tooltip {...tooltip}>
        {/* We define a <div /> with an inline class as the Tooltip cannot determine the position for a native React element */}
        {/* We also define the <div /> as inline element to follow the <button /> display rule */}
        <div className='inline'>{buttonElement}</div>
      </Tooltip>
    );
  }

  return buttonElement;
}
